import { memo, useRef, type ComponentProps } from 'react'

import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import StopTimingEditControl from '../StopTimingEditControl'
import StopSearchActions from './StopSearchActions'
import { TRIP_LIST_MOBILE_INLINE_PADDING } from './tripListLayout'
import type TripListView from './TripListView'
import type { TripListStopHeader } from './tripListTypes'

type TripListViewProps = ComponentProps<typeof TripListView>

const STOP_HEADER_PADDING = `8 ${TRIP_LIST_MOBILE_INLINE_PADDING}` as const

interface Props {
  groupId: string
  stopHeader: TripListStopHeader
  addDaysOrDatesLabel: string
  actionsDisabled: boolean
  isSelectionMode: boolean
  onSendStopTimingEditMessage: TripListViewProps['onSendStopTimingEditMessage']
  onOpenStopHotels: TripListViewProps['onOpenStopHotels']
  onOpenStopFlights: TripListViewProps['onOpenStopFlights']
  onOpenStopExperiences: TripListViewProps['onOpenStopExperiences']
}

function TripListMobileStopHeader({
  groupId,
  stopHeader,
  addDaysOrDatesLabel,
  actionsDisabled,
  isSelectionMode,
  onSendStopTimingEditMessage,
  onOpenStopHotels,
  onOpenStopFlights,
  onOpenStopExperiences,
}: Props) {
  // The timing dropdown anchors to the title row so it opens beneath the stop
  // timing rather than below the search chips.
  const titleRowRef = useRef<HTMLDivElement>(null)

  return (
    <Sheet
      background="light-background"
      padding={STOP_HEADER_PADDING}
      data-testid="trip-list-mobile-stop-header"
      data-stop-id={stopHeader.stop.id}
      data-trip-list-group-target-id={groupId}
      data-trip-list-group-target-placement="before"
    >
      <Group direction="vertical" gap={8}>
        <Group ref={titleRowRef} direction="horizontal" wrap="wrap" verticalAlign="baseline" gap={8}>
          <BodyText variant="medium" weight="bold" lineClamp={1}>
            {stopHeader.title}
          </BodyText>
          <Caption variant="large">
            <StopTimingEditControl
              stop={stopHeader.stop}
              onSendMessage={onSendStopTimingEditMessage}
              disabled={actionsDisabled || isSelectionMode}
              variant="mobile"
              dropdownAnchorRef={titleRowRef}
              expandTapTarget
            >
              {stopHeader.subtitle || addDaysOrDatesLabel}
            </StopTimingEditControl>
          </Caption>
        </Group>
        {stopHeader.canSearch && (
          <Group direction="horizontal" wrap="wrap" gap={8}>
            <StopSearchActions
              stop={stopHeader.stop}
              disabled={isSelectionMode}
              onOpenStopHotels={onOpenStopHotels}
              onOpenStopFlights={onOpenStopFlights}
              onOpenStopExperiences={onOpenStopExperiences}
            />
          </Group>
        )}
      </Group>
    </Sheet>
  )
}

// The list re-renders on every drag-hover step; see TripListMobileRowCard.
export default memo(TripListMobileStopHeader)
